/**
 * Foul details: validation, team-foul / player-foul classification, free throws awarded,
 * and per-player foul tallies. All rule differences come from RuleSet data.
 */
import type { RuleSet } from '../rules/ruleset';
import type { FoulType } from '../types';
import { isFoulType, isValidPlayerNumber } from '../types';

/** Optional details of a foul. A foul without any details is valid ("save without details"). */
export interface FoulDetails {
  /** Jersey number of the fouling player, "0"–"99" or "00". */
  playerNumber?: string;
  /** Coach / bench foul (no player). */
  bench?: boolean;
  foulType?: FoulType;
  /** Shooting fouls only: value of the attempted shot. */
  shotPoints?: 2 | 3;
  /** Shooting fouls only: true = basket counted (and-one). */
  basketMade?: boolean;
}

export type FoulDetailsError =
  | 'INVALID_PLAYER_NUMBER'
  | 'INVALID_FOUL_TYPE'
  | 'INVALID_SHOT_POINTS'
  | 'INVALID_FOUL_DETAILS';

/** Foul types a bench / coach foul may have. */
export const BENCH_FOUL_TYPES: readonly FoulType[] = ['technical', 'disqualifying'];

/** Foul types that can be on a shooter, with the free throws of Art. 34, 37, 38 and 39. */
export const SHOOTER_FOUL_TYPES: readonly FoulType[] = [
  'shooting',
  'disruptive',
  'flagrant',
  'disqualifying',
];

export function validateFoulDetails(d: FoulDetails): FoulDetailsError | null {
  if (d.playerNumber !== undefined && !isValidPlayerNumber(d.playerNumber)) {
    return 'INVALID_PLAYER_NUMBER';
  }
  if (d.foulType !== undefined && !isFoulType(d.foulType)) return 'INVALID_FOUL_TYPE';
  if (d.bench !== undefined && typeof d.bench !== 'boolean') return 'INVALID_FOUL_DETAILS';
  if (d.bench === true) {
    if (d.playerNumber !== undefined) return 'INVALID_FOUL_DETAILS';
    if (d.foulType !== undefined && BENCH_FOUL_TYPES.indexOf(d.foulType) === -1) {
      return 'INVALID_FOUL_DETAILS';
    }
  }
  const hasShot = d.shotPoints !== undefined || d.basketMade !== undefined;
  const canHaveShot =
    d.bench !== true && d.foulType !== undefined && SHOOTER_FOUL_TYPES.indexOf(d.foulType) !== -1;
  if (d.foulType === 'shooting' || (hasShot && canHaveShot)) {
    if (d.shotPoints !== 2 && d.shotPoints !== 3) return 'INVALID_SHOT_POINTS';
    if (typeof d.basketMade !== 'boolean') return 'INVALID_FOUL_DETAILS';
  } else if (hasShot) {
    return 'INVALID_FOUL_DETAILS';
  }
  return null;
}

/**
 * Does this foul count as a team foul (toward the team-foul penalty)?
 * Bench/coach fouls never do. Player fouls do unless their type is in
 * rules.teamFoulExcludedTypes (NBA: technical, offensive). A foul without a type counts.
 */
export function countsAsTeamFoul(rules: RuleSet, d: FoulDetails): boolean {
  if (d.bench === true) return false;
  if (d.foulType === undefined) return true;
  return rules.teamFoulExcludedTypes.indexOf(d.foulType) === -1;
}

/** Does this player foul count toward rules.personalFoulLimit? (NBA: technicals do not.) */
export function countsTowardFoulLimit(rules: RuleSet, foulType: FoulType | undefined): boolean {
  if (foulType === undefined) return true;
  return rules.personalFoulExcludedTypes.indexOf(foulType) === -1;
}

/**
 * Free throws awarded to the opponent for this foul.
 * on a shooter (any type with shot details): made → 1, missed → shotPoints · personal → 2 if the fouling team was already in
 * the penalty before this foul, else 0 · offensive → 0 · technical → 1 ·
 * disruptive / flagrant / disqualifying (and older unsportsmanlike, flagrant 1 / 2) → 2.
 * No type: a bench foul is treated as technical (1), a player foul as personal.
 */
export function freeThrowsAwarded(d: FoulDetails, teamInPenaltyBefore: boolean): number {
  const type: FoulType = d.foulType ?? (d.bench === true ? 'technical' : 'personal');
  // On a shooter: the basket plus 1, otherwise the value of the shot.
  if (d.shotPoints !== undefined) return d.basketMade === true ? 1 : d.shotPoints;
  switch (type) {
    case 'personal':
      return teamInPenaltyBefore ? 2 : 0;
    case 'shooting':
      return 2;
    case 'offensive':
      return 0;
    case 'technical':
      return 1;
    case 'disruptive':
    case 'flagrant':
    case 'unsportsmanlike':
    case 'disqualifying':
    case 'flagrant1':
    case 'flagrant2':
      return 2;
  }
}

/**
 * What follows the last free throw of a foul's set (FIBA Art. 29.2.6, 36.3.2, 37-39, 44):
 * - rebound: personal and shooting fouls; a made shot goes to the other team, a missed one is live
 * - throwIn: disruptive, flagrant, disqualifying; the shooting team keeps the ball, shot clock 14
 * - resume: technical; play continues from where it stopped, nothing changes
 */
export type FreeThrowThen = 'rebound' | 'throwIn' | 'resume';

export function freeThrowThen(d: FoulDetails): FreeThrowThen {
  const type: FoulType = d.foulType ?? (d.bench === true ? 'technical' : 'personal');
  switch (type) {
    case 'personal':
    case 'shooting':
    case 'offensive':
      return 'rebound';
    case 'technical':
      return 'resume';
    default:
      return 'throwIn';
  }
}

export type FoulOutReason = 'FOUL_LIMIT' | 'DISQUALIFIED';

export interface PlayerFoulTally {
  playerNumber: string;
  /** Every foul charged to the player. */
  total: number;
  /** Fouls that count toward `limit`. */
  counted: number;
  limit: number;
  /** Fouls by type; untyped fouls are under 'unspecified'. */
  byType: Partial<Record<FoulType | 'unspecified', number>>;
  /** An ejection rule was hit (disqualifying / flagrant 2 / 2 technicals ...). */
  disqualified: boolean;
  /** Player may not continue: limit reached or disqualified. */
  fouledOut: boolean;
  /** Why the player is out (the first reason that happened); null while still eligible. */
  outReason: FoulOutReason | null;
}

export function emptyPlayerFoulTally(rules: RuleSet, playerNumber: string): PlayerFoulTally {
  return {
    playerNumber,
    total: 0,
    counted: 0,
    limit: rules.personalFoulLimit,
    byType: {},
    disqualified: false,
    fouledOut: false,
    outReason: null,
  };
}

/** Returns a new tally with one more foul of `foulType`. */
export function addPlayerFoul(
  rules: RuleSet,
  tally: PlayerFoulTally,
  foulType: FoulType | undefined,
): PlayerFoulTally {
  const key = foulType ?? 'unspecified';
  const byType = { ...tally.byType, [key]: (tally.byType[key] ?? 0) + 1 };
  const counted = tally.counted + (countsTowardFoulLimit(rules, foulType) ? 1 : 0);
  let disqualified =
    tally.disqualified ||
    (foulType !== undefined && rules.ejectionFoulTypes.indexOf(foulType) !== -1);
  for (const combo of rules.ejectionCombos) {
    let n = 0;
    for (const t of combo.types) n += byType[t] ?? 0;
    if (n >= combo.count) disqualified = true;
  }
  const outReason: FoulOutReason | null =
    tally.outReason ??
    (disqualified ? 'DISQUALIFIED' : counted >= tally.limit ? 'FOUL_LIMIT' : null);
  return {
    ...tally,
    total: tally.total + 1,
    counted,
    byType,
    disqualified,
    fouledOut: outReason !== null,
    outReason,
  };
}
